import { FC, useState } from 'react';

import { useMeetTools } from '../../../features/meet/MeetToolsProvider';
import { useAppDispatch } from '../../../hooks/redux';
import { clearEmotionsClientSelections } from '../../../store/emotionsGroup.slice';

import { ConfirmDialog, Button } from '../../../shared/ui';

import { EmotionsGroupPsychologistResults } from './emotions-group-psychologist-results';

const EMOTIONS_CONFIRM_CLOSE = {
  name: '«Детектор эмоций»',
  description: 'Клиенты перестанут видеть выбор эмоций.',
};

export interface EmotionsGroupPanelProps {
  onClose: () => void;
  onConfirm?: () => void;
  sendEmotionsOpen?: () => void;
  sendEmotionsClose?: () => void;
  expectedParticipantIds: string[];
  participantDisplayNames?: Record<string, string>;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба — для трансляции эмоций на «общий экран» клиентам */
  onActiveTabChange?: (participantLabel: string, emo: string[]) => void;
}

/**
 * Нижняя панель Детектора эмоций для психолога в групповой терапии.
 * Первый экран: кнопка «Запросить» → отправка emotions_open и переход к результатам.
 * Второй экран: счётчик ответивших и табы по участникам. Закрытие — модалка и sendEmotionsClose.
 */
export const EmotionsGroupPanel: FC<EmotionsGroupPanelProps> = ({
  onClose,
  onConfirm,
  sendEmotionsOpen,
  sendEmotionsClose,
  expectedParticipantIds,
  participantDisplayNames,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const dispatch = useAppDispatch();
  const tools = useMeetTools();
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  const handleConfirm = () => {
    /**
     * Снимок участников храним в провайдере встречи, чтобы состояние
     * не сбрасывалось при временной перестройке layout (например, screen share).
     */
    tools.setEmotionsGroupExpectedParticipantIds([...expectedParticipantIds]);
    tools.setEmotionsGroupParticipantDisplayNames({ ...participantDisplayNames });
    dispatch(clearEmotionsClientSelections());
    sendEmotionsOpen?.();
    tools.setEmotionsGroupResultsShown(true);
    onConfirm?.();
  };

  const handleRequestClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(false);
    sendEmotionsClose?.();
    onClose();
  };

  const handleCancelClose = () => {
    setIsConfirmCloseOpen(false);
  };

  const confirmMessage = `Вы действительно хотите закрыть инструмент ${EMOTIONS_CONFIRM_CLOSE.name}? ${EMOTIONS_CONFIRM_CLOSE.description}`;

  const confirmCloseModal = (
    <ConfirmDialog
      isOpen={isConfirmCloseOpen}
      title="Закрытие инструмента"
      message={confirmMessage}
      cancelText="Оставить открытым"
      confirmText="Закрыть инструмент"
      onCancel={handleCancelClose}
      onConfirm={handleConfirmClose}
    />
  );

  if (tools.emotionsGroupResultsShown) {
    return (
      <>
        <EmotionsGroupPsychologistResults
          expectedParticipantIds={tools.emotionsGroupExpectedParticipantIds}
          participantDisplayNames={tools.emotionsGroupParticipantDisplayNames}
          onClose={handleRequestClose}
          isExpanded={isExpanded}
          onExpandToggle={onExpandToggle}
          onActiveTabChange={onActiveTabChange}
        />
        {confirmCloseModal}
      </>
    );
  }

  return (
    <>
      <div className="h-full w-full bg-white rounded-chip md:rounded-card overflow-hidden flex flex-col p-4">
        <div className="text-display-s text-ink mb-4">Детектор эмоций</div>
        <div className="text-ink-muted text-body-l mb-6">
          Запросить у участников группы их текущее эмоциональное состояние. У каждого откроется выбор эмоций.
        </div>
        <div className="mt-auto flex flex-row">
          <Button size="md" variant="primary" fullWidth onClick={handleConfirm}>
            Запросить
          </Button>
        </div>
      </div>
      {confirmCloseModal}
    </>
  );
};
